import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

function TaxRateManagementSection() {
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTaxRate, setEditingTaxRate] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [newTaxRate, setNewTaxRate] = useState({
    name: '',
    code: '',
    percentage: '',
    tax_included: true,
    apply_to_products: true,
    apply_to_shipping: true,
    apply_to_coupons: true
  });

  useEffect(() => {
    fetchTaxRates();
  }, []);

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/admin/tax-rates');
      setTaxRates(response.data.tax_rates || []);
    } catch (error) {
      console.error('Failed to fetch tax rates:', error);
    }
  };

  const startEditingTaxRate = (taxRate) => {
    setEditingTaxRate(taxRate.id);
    setEditValues({
      name: taxRate.name,
      code: taxRate.code,
      percentage: taxRate.percentage.toString(),
      tax_included: taxRate.tax_included,
      apply_to_products: taxRate.apply_to_products,
      apply_to_shipping: taxRate.apply_to_shipping,
      apply_to_coupons: taxRate.apply_to_coupons || false
    });
  };

  const handleUpdateTaxRate = async (taxRateId) => {
    setLoading(true);
    try {
      await axios.put(`/admin/tax-rates/${taxRateId}`, {
        ...editValues,
        percentage: parseFloat(editValues.percentage)
      });
      toast.success('✅ Tax rate updated successfully');
      setEditingTaxRate(null);
      setEditValues({});
      fetchTaxRates();
    } catch (error) {
      console.error('Failed to update tax rate:', error);
      toast.error('Failed to update tax rate');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTaxRate = async (taxRateId) => {
    if (!window.confirm('Are you sure you want to delete this tax rate?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/tax-rates/${taxRateId}`);
      toast.success('✅ Tax rate deleted successfully');
      fetchTaxRates();
    } catch (error) {
      console.error('Failed to delete tax rate:', error);
      toast.error('Failed to delete tax rate');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTaxRate = async () => {
    if (!newTaxRate.name || !newTaxRate.code || !newTaxRate.percentage) {
      toast.error('Please fill in name, code, and percentage');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/admin/tax-rates', {
        ...newTaxRate,
        percentage: parseFloat(newTaxRate.percentage),
        active: true
      });
      toast.success('✅ Tax rate added successfully');
      setNewTaxRate({
        name: '',
        code: '',
        percentage: '',
        tax_included: true,
        apply_to_products: true,
        apply_to_shipping: true
      });
      fetchTaxRates();
    } catch (error) {
      console.error('Failed to add tax rate:', error);
      toast.error('Failed to add tax rate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tax Rate Management</h2>
        <p className="text-gray-600">Manage tax rates for products and shipping</p>
      </div>

      {/* Add New Tax Rate */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Tax Rate</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Tax Name (e.g., GST)"
              value={newTaxRate.name}
              onChange={(e) => setNewTaxRate(prev => ({ ...prev, name: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Tax Code (e.g., AU-GST)"
              value={newTaxRate.code}
              onChange={(e) => setNewTaxRate(prev => ({ ...prev, code: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="number"
              step="0.01"
              placeholder="Percentage (e.g., 10)"
              value={newTaxRate.percentage}
              onChange={(e) => setNewTaxRate(prev => ({ ...prev, percentage: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newTaxRate.tax_included}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, tax_included: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span>Tax Included in Prices</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newTaxRate.apply_to_products}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, apply_to_products: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span>Apply to All Products</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newTaxRate.apply_to_shipping}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, apply_to_shipping: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span>Apply to Shipping</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newTaxRate.apply_to_coupons}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, apply_to_coupons: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span>Apply to Coupons</span>
            </label>
          </div>
          <Button 
            onClick={handleAddTaxRate}
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {loading ? 'Adding...' : 'Add Tax Rate'}
          </Button>
        </div>
      </Card>

      {/* Current Tax Rates */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Tax Rates ({taxRates.length})</h3>
        {taxRates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No tax rates configured</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2">Name</th>
                  <th className="text-left py-3 px-2">Code</th>
                  <th className="text-left py-3 px-2">Rate</th>
                  <th className="text-left py-3 px-2">Settings</th>
                  <th className="text-left py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {taxRates.map((rate) => (
                  <tr key={rate.id} className="border-b border-gray-100">
                    {editingTaxRate === rate.id ? (
                      <>
                        <td className="py-3 px-2">
                          <input
                            type="text"
                            value={editValues.name}
                            onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-2 py-1 border rounded"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="text"
                            value={editValues.code}
                            onChange={(e) => setEditValues(prev => ({ ...prev, code: e.target.value }))}
                            className="w-full px-2 py-1 border rounded"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="number"
                            step="0.01"
                            value={editValues.percentage}
                            onChange={(e) => setEditValues(prev => ({ ...prev, percentage: e.target.value }))}
                            className="w-full px-2 py-1 border rounded"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <div className="space-y-1">
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={editValues.tax_included}
                                onChange={(e) => setEditValues(prev => ({ ...prev, tax_included: e.target.checked }))}
                                className="w-3 h-3"
                              />
                              Included
                            </label>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={editValues.apply_to_products}
                                onChange={(e) => setEditValues(prev => ({ ...prev, apply_to_products: e.target.checked }))}
                                className="w-3 h-3"
                              />
                              Products
                            </label>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={editValues.apply_to_shipping}
                                onChange={(e) => setEditValues(prev => ({ ...prev, apply_to_shipping: e.target.checked }))}
                                className="w-3 h-3"
                              />
                              Shipping
                            </label>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={editValues.apply_to_coupons}
                                onChange={(e) => setEditValues(prev => ({ ...prev, apply_to_coupons: e.target.checked }))}
                                className="w-3 h-3"
                              />
                              Coupons
                            </label>
                          </div>
                        </td>
                        <td className="py-3 px-2 space-x-1">
                          <Button size="sm" onClick={() => handleUpdateTaxRate(rate.id)} className="bg-custom-orange hover:bg-custom-orange">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingTaxRate(null)}>Cancel</Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-2 font-medium">{rate.name}</td>
                        <td className="py-3 px-2">{rate.code}</td>
                        <td className="py-3 px-2">{rate.percentage}%</td>
                        <td className="py-3 px-2">
                          <div className="flex flex-wrap gap-1">
                            {rate.tax_included && <Badge variant="outline" className="text-xs">Included</Badge>}
                            {rate.apply_to_products && <Badge variant="outline" className="text-xs">Products</Badge>}
                            {rate.apply_to_shipping && <Badge variant="outline" className="text-xs">Shipping</Badge>}
                            {rate.apply_to_coupons && <Badge variant="outline" className="text-xs">Coupons</Badge>}
                          </div>
                        </td>
                        <td className="py-3 px-2 space-x-2">
                          <Button size="sm" variant="outline" onClick={() => startEditingTaxRate(rate)}>Edit</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteTaxRate(rate.id)} className="bg-red-600">Delete</Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default TaxRateManagementSection;